<template>
  <div v-if="show" class="education-dialog" :class="hasResumeView ? 'has-resume-view' : ''">
    <div class="education-list-editor">
      <div v-for="(education, index) in educationList" class="education-table" :class="!education.showEdit && 'show-table'" :key="education.id">
        <div class="head">
          <div class="title" @click="editorEducation(education, index)">{{ `${$t(`${i18nPrefix}.title.education`)} ${index + 1}` }}</div>
          <div class="fold-btn" :class="education.showEdit ? 'rotated-icon' : ''" @click="editorEducation(education, index)">
            <i class="iconfont icondown"></i>
          </div>
          <el-popconfirm
            :confirm-button-text="$t('common.btn.confirm')"
            :cancel-button-text="$t('common.btn.cancel')"
            icon="el-icon-info"
            icon-color="red"
            :title="$t('common.message.confirmDelete')"
            @confirm="deleteEducation(education, index)"
          >
            <template #reference>
              <div class="delete-btn">
                <i class="el-icon-close"></i>
              </div>
            </template>
          </el-popconfirm>
        </div>
        <div class="content">
          <el-table
            v-if="!education.showEdit"
            stripe
            row-key="_vid"
            :show-header="false"
            :data="[education]"
            :row-class-name="getRowClass"
          >
            <el-table-column v-if="checkFieldShow('startDate')" class-name="start-date" prop="startDate" :width="hasResumeView ? '90px' : '100px'" :formatter="formatDate"></el-table-column>
            <el-table-column v-if="checkFieldShow('endDate')" prop="endDate" :width="hasResumeView ? '90px' : '100px'" :formatter="formatDate"></el-table-column>
            <el-table-column v-if="checkFieldShow('schoolName')" prop="schoolName" :class-name="isMiniResume ? 'school-name' : ''"></el-table-column>
            <el-table-column v-if="checkFieldShow('majorName')" prop="majorName"></el-table-column>
          </el-table>
          <EducationEditor
            v-else
            class="education-editor editor"
            :ref="`educationEditor_${index}`"
            :education="education"
            :levelList="educationLevelList"
            :academicRatingList="academicRatingList"
            :majorList="majorList"
            :schoolList="schoolList"
            :requiredField="requiredField"
            :hiddenField="hiddenField"
            :displayField="displayField"
            :isCandidate="isCandidate"
            :isMiniResume="isMiniResume"
            :searchSchoolList="searchSchoolList"
            :searchMajorList="searchMajorList"
          />
        </div>
      </div>
      <div class="add-btn-group">
        <el-button
          class="add-btn"
          type="text"
          icon="iconfont iconjia"
          @click="handleAddBtnClick"
        >
          {{ $t(`${this.i18nPrefix}.btn.addEducationBackground`) }}
        </el-button>
      </div>
      <div v-if="educationEditor.showTips" class="education-tips">
        {{ $t(educationEditor.tipi18n) }}
      </div>
    </div>
    <RectLoading v-show="loading" class="loading"></RectLoading>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import mixin from './mixin'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import EducationEditor from '@/components/candidate/editor/education.vue'
import RectLoading from '@/components/loading/rectLoading.vue'

let vid = 0

export default {
  props: {
    setting: {
      type: Object,
      required: false
    },
    isMiniResume: {
      type: Boolean,
      default: false
    },
    hasResumeView: {
      type: Boolean,
      default: false
    },
    showEdit: {
      type: Boolean,
      default: false
    }
  },
  mixins: [mixin],
  components: {
    EducationEditor,
    RectLoading
  },
  data () {
    const i18nPrefix = 'admin.application.candidate'
    return {
      hasForm: false,
      i18nPrefix,
      dataLoader: null,
      educationLevelList: [],
      academicRatingList: [],
      majorList: [],
      schoolList: [],
      monthList: [],
      yearList: [],
      educationList: [],
      isEditorEducation: {
        isEditor: false,
        index: 0
      },
      educationEditor: {
        showTips: false,
        tipi18n: `${i18nPrefix}.message.fieldRequired`
      },
      stepData: {}
    }
  },
  computed: {
    requiredField () {
      // hr not need any check
      if (!this.isCandidate) {
        return []
      }
      const field = ['degreeLevel', 'schoolName']
      if (!this.isMiniResume) {
        field.push(...['majorName', 'startDate', 'endDate'])
      }
      return field
    },
    hiddenField () {
      if (this.isMiniResume) {
        const hiddenField = [
          'startDate',
          'endDate',
          'academicRating',
          'gpa',
          'summary'
        ]
        if (this.setting?.hiddenField) {
          hiddenField.push(...this.setting?.hiddenField)
        }
        return hiddenField
      }
      return this.setting?.hiddenField || []
    },
    displayField () {
      return this.setting?.displayField || []
    },
    isCandidate () {
      if (this.setting?.isCandidate) {
        return this.setting?.isCandidate
      }
      return false
    },
    requiredCount () {
      if (this.setting?.requiredCount) {
        return this.setting?.requiredCount
      }
      return 0
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    ...mapActions('application', ['getEducationLevelList', 'getAcademicRatingList', 'getMajorList', 'getSchoolList']),
    formatDate (row, column, cellValue, index) {
      if (cellValue) {
        return moment(cellValue).format('YYYY-MM')
      } else if (column.property === 'endDate' && row.startDate) {
        return this.$t('admin.application.candidate.label.tillNow')
      } else {
        return '-'
      }
    },
    async loadData () {
      if (!this.dataLoader) {
        this.dataLoader = Promise.all([
          this.loadEducationLevelList(),
          this.loadAcademicRatingList()
        ])
      }
      await this.dataLoader
    },
    checkEducationList () {
      if (!this.educationList || this.educationList.length === 0) {
        return
      }
      _.forEach(this.educationList, item => {
        let valid = true
        const requiredField = [...this.requiredField]
        if (['Junior School', 'High School'].includes(item.degreeLevel)) {
          requiredField.splice(requiredField.indexOf('majorName'), 1)
        }
        _.forEach(requiredField, fieldName => {
          if (_.isNil(item[fieldName]) || item[fieldName] === '') {
            valid = false
          }
        })
        this.$set(item, 'valid', valid)
      })
    },
    async loadEducationLevelList () {
      const resp = await this.getEducationLevelList()
      if (resp) {
        this.educationLevelList = _.map(resp.education_levels, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'en_name')
          return item
        })
      }
    },
    async loadAcademicRatingList () {
      const resp = await this.getAcademicRatingList()
      if (resp) {
        this.academicRatingList = _.map(resp.academic_ratings, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'en_name')
          return item
        })
      }
    },
    async searchMajorList (keywords) {
      if (!keywords || !keywords.trim()) {
        return
      }
      const params = {
        keywords: keywords
      }
      const resp = await this.getMajorList(params)
      if (resp) {
        this.majorList = _.map(resp.majors, item => {
          addI18nProperty(item, 'value', 'cn_name', 'name')
          return item
        })
      }
      return this.majorList
    },
    async searchSchoolList (keywords) {
      if (!keywords || !keywords.trim()) {
        return
      }
      const params = {
        keywords: keywords
      }
      const resp = await this.getSchoolList(params)
      if (resp) {
        this.schoolList = _.map(resp.schools, item => {
          addI18nProperty(item, 'value', 'cn_name', 'name')
          return item
        })
      }
      return this.schoolList
    },
    async editorEducation (rowData, index) {
      await this.loadData()
      const showEdit = rowData.showEdit
      if (showEdit) {
        const data = await this.$refs[`educationEditor_${index}`][0]?.submit()
        if (!data) {
          return
        }
        rowData.valid = true
        _.merge(rowData, data)
      }
      rowData.showEdit = !showEdit
    },
    deleteEducation (rowData, index) {
      this.educationList.splice(index, 1)
    },
    async handleAddBtnClick () {
      await this.loadData()
      const education = {}
      education._vid = ++vid
      education.valid = false
      education.showEdit = true
      this.educationList.push(education)
    },
    async setStepDataToCandidateInfo () {
      const candidateInfo = this.candidateInfo
      this.$set(candidateInfo, 'educationInfo', _.map(this.educationList, item => {
        const info = { ...item }
        _.omit(info, [
          '_vid',
          'valid'
        ])
        return info
      }))
    },
    async getStepData () {
      await this.loadData()
      const educationInfo = this.candidateInfo.educationInfo
      if (educationInfo) {
        this.educationList = _.map(educationInfo, (item, index) => {
          const major = item.majorId && _.find(this.majorList, { id: item.majorId })
          const school = item.schoolId && _.find(this.schoolList, { id: item.schoolId })
          return {
            ...item,
            majorName: major?.i18nName || item.majorName,
            schoolName: school?.i18nName || item.schoolName,
            _vid: index + 1,
            showEdit: _.isBoolean(item.showEdit) ? item.showEdit : this.showEdit
          }
        })
        vid = this.educationList.length
      }
      return this.educationList
    },
    async customValidate () {
      const length = this.educationList.length
      let isValid = true
      await Promise.all(_.times(length, async index => {
        const educationRef = this.$refs[`educationEditor_${index}`]
        if (educationRef && educationRef.length > 0) {
          const data = await educationRef[0].submit()
          if (data) {
            _.merge(this.educationList[index], data)
          } else {
            isValid = false
          }
        }
      }))
      return isValid
    },
    async validate () {
      let isValid = true
      if (this.hasForm) {
        isValid = await this.$refs.form.submit()
      }
      if (isValid) {
        isValid = await this.customValidate()
      }
      isValid = await this.validateEducationList()
      if (this.educationList.length < this.requiredCount) {
        this.educationEditor.showTips = true
        this.educationEditor.tipi18n = `${this.i18nPrefix}.message.educationCountRequired`
        isValid = false
      }
      return Boolean(isValid)
    },
    async validateEducationList () {
      let isValid = true
      _.forEach(this.educationList, item => {
        const requiredField = [...this.requiredField]
        if (['Junior School', 'High School'].includes(item.degreeLevel)) {
          requiredField.splice(requiredField.indexOf('majorName'), 1)
        }
        _.forEach(requiredField, fieldName => {
          if (_.isNil(item[fieldName]) || item[fieldName] === '') {
            isValid = false
            this.educationEditor.showTips = true
            return false
          }
        })
        if (!isValid) {
          return false
        }
      })
      return isValid
    },
    getRowClass ({ row }) {
      if (row && !row.valid) {
        return 'red'
      }
      return ''
    },
    checkFieldShow (fieldName) {
      return !_.includes(this.hiddenField, fieldName)
    }
  },
  watch: {
    educationList (val) {
      this.checkEducationList()
      if (this.educationList.length >= this.requiredCount) {
        this.educationEditor.showTips = false
        this.educationEditor.tipi18n = `${this.i18nPrefix}.message.fieldRequired`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.education-dialog {
  position: relative;

  .education-list-editor {
    padding: 0 20px;

    .education-tips {
      padding: 20px 0;
      color: #ff0000;
      text-align: right;
    }

    .education-list {
      margin-top: 20px;
      margin-bottom: 40px;

      .list-item {
        padding: 5px 10px;
        border: 1px solid #e7e7e7;
        background: #f1f1f1;

        &:not(:first-child) {
          margin-top: 10px;
        }

        &.ghost {
          opacity: 0.5;
          background-color: #cecece;
        }

        .item-title {
          display: flex;
          line-height: 16px;

          .item-icon {
            line-height: 20px;

            .icon {
              display: inline-block;
              width: 14px;
              height: 14px;
              background-repeat: no-repeat;
              background-size: contain;
            }

            .text-icon {
              background-image: url(~@/assets/image/openQuestion/text.png);
            }

            .select-icon {
              background-image: url(~@/assets/image/openQuestion/select.png);
            }

            .checkbox-icon {
              background-image: url(~@/assets/image/openQuestion/checkbox.png);
            }

            .attachment-icon {
              background-image: url(~@/assets/image/openQuestion/attachment.png);
            }

            .video-icon {
              background-image: url(~@/assets/image/openQuestion/video.png);
            }
          }

          .item-name {
            flex: 1;
            margin-left: 10px;
            color: #3b3b3b;
          }

          .item-btn-group {
            display: flex;
            color: #0096cc;
            align-items: center;

            .item-btn {
              cursor: pointer;

              i {
                font-size: 18px;
              }

              &:not(:first-child) {
                margin-left: 10px;
              }
            }

            .drag-btn {
              cursor: grab;
            }

            .visibility-btn {
              color: inherit;
              font-size: 12px;

              .btn-inner-container {
                .btn-label {
                  vertical-align: middle;
                }

                .btn-icon {
                  font-size: 14px;
                  vertical-align: middle;
                }
              }
            }
          }
        }
        .item-options-container {
          color: #676767;

          .option-list {
            padding-left: 15px;

            .option-item {
              padding: 3px 0;

              &::before {
                content: '-';
                margin-right: 10px;
              }

              &.knockout {
                color: #F56C6C;
              }

              .knockout-tag {
                height: auto;
                margin: 0 10px;
                padding: 1px 5px;
                border-radius: 15px;
                line-height: normal;
              }
            }
          }
        }
      }
    }

    .add-btn-group {
      .add-btn {
        display: inline-block;
        width: 100%;
        margin: 16px auto 0;
        padding: 14px 0;
        border: 1px dashed #0096cc;
        border-radius: 0;
        font-size: 14px;

        ::v-deep .iconfont {
          font-size: 14px;
        }

        &:first-child {
          margin-top: 24px;
        }
      }
    }

    .education-table {
      border: 1px solid #e4e7ed;
      margin-bottom: 8px;

      .head {
        display: flex;
        align-items: center;
        height: 40px;
        line-height: 40px;
        padding: 0 25px;
        border-bottom: 1px solid #dcdfe6;

        .title {
          flex: 1;
          display: inline-block;
          color: #606266;
          font-size: 15px;
          cursor: pointer;
        }

        .fold-btn {
          display: inline-block;
          transition: transform 0.3s;

          &:hover {
            cursor: pointer;
          }
        }

        .rotated-icon {
          transform: rotate(180deg);
        }

        .delete-btn {
          position: relative;
          display: inline-block;
          right: -10px;

          &:hover {
            cursor: pointer;
          }
        }
      }

      .content {
        ::v-deep {
          .start-date {
            .cell {
              padding-left: 25px;
            }
          }
          .school-name {
            .cell {
              padding-left: 25px;
            }
          }
        }

        .education-editor {
          margin: 25px 70px 0;
        }
      }

      ::v-deep {
        .el-table__row {
          &.red {
            color: #ff0000;
          }
          background-color: #f7f7f7;
        }

        .el-table .cell {
          word-break: break-word;
          white-space: normal;
        }
      }

    }

    .show-table {
      border-bottom: 0;
    }

    ::v-deep {
      .el-dialog {
        width: 63vw;
        background-color: #f7f7f7;
      }
      .el-dialog__footer{
        padding: 10px 55px 20px;
      }
    }

    .editor-dialog {
      .form-container {
        width: 80%;
        margin: auto;
      }

      .editor-footer {
        display: flex;
        justify-content: space-between;
      }
    }
  }

  .loading {
    position: absolute;
  }
}

.has-resume-view {
  .education-list-editor {
    width: 90%;
    margin-left: 34px;

    .education-table {
      .el-table {
        font-size: 12px;

        .el-button {
          font-size: 12px;
        }
      }
    }
  }
}
</style>
